/**
 * Fetch Blogs Feature
 * 100% Isolated: Fetch blogs functionality that can be changed independently
 */
import { useState, useCallback } from 'react';
import { blogApiService, mapBackendBlogToPost } from '../api';
import type { BlogPost, BlogFilters, BackendBlogRaw } from '../api';
import { logger } from '@/shared/utils';

export const useFetchBlogs = () => {
  const [blogs, setBlogs] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState<{ page: number; per_page: number; total: number } | null>(null);

  const fetchBlogs = useCallback(async (filters: BlogFilters = {}): Promise<BlogPost[]> => {
    setLoading(true);
    setError(null);

    logger.debug('Starting blog fetch', 'useFetchBlogs', { filters });

    try {
      const response = await blogApiService.getBlogs(filters);
      
      logger.debug('Received API response', 'useFetchBlogs', { 
        success: response.success,
        hasData: !!response.data,
        dataKeys: response.data ? Object.keys(response.data) : [],
        fullResponse: response
      });
      
      if (!response.success || !response.data) {
        const errorMsg = response.error?.message || 'Failed to fetch blogs';
        logger.error('Blog fetch failed', 'useFetchBlogs', { 
          response, 
          filters,
          error: response.error
        });
        throw new Error(errorMsg);
      }
      
      // Extract blog data from response
      // Backend returns: { success: true, data: { blogs: [...], total: int, page: int, per_page: int } }
      const blogData = response.data;
      
      // DEBUG: Log the data extraction
      logger.debug('Extracting blog data', 'useFetchBlogs', {
        blogData,
        blogDataType: typeof blogData,
        blogDataKeys: blogData ? Object.keys(blogData) : [],
        blogDataBlogs: blogData?.blogs,
        blogDataBlogsType: typeof blogData?.blogs,
        blogDataBlogsIsArray: Array.isArray(blogData?.blogs),
        blogDataBlogsLength: Array.isArray(blogData?.blogs) ? blogData.blogs.length : 'not an array',
        blogDataTotal: blogData?.total,
        blogDataPage: blogData?.page,
        blogDataPerPage: blogData?.per_page
      });
      
      // Type assertion: backend returns BlogSummaryResponse[], not BlogPost[]
      const rawBlogs = (blogData?.blogs || []) as unknown as BackendBlogRaw[];
      const total = blogData?.total || 0;
      const page = blogData?.page || 1;
      const per_page = blogData?.per_page || 10;
      
      logger.debug('After extraction', 'useFetchBlogs', {
        rawBlogs,
        rawBlogsLength: rawBlogs.length,
        rawBlogsType: typeof rawBlogs,
        rawBlogsIsArray: Array.isArray(rawBlogs),
        total,
        page,
        per_page,
        firstBlogSample: rawBlogs[0] || null,
        firstBlogSampleKeys: rawBlogs[0] ? Object.keys(rawBlogs[0]) : []
      });
      
      logger.debug('Extracted blog data', 'useFetchBlogs', {
        rawBlogsCount: rawBlogs.length,
        total,
        page,
        per_page,
        firstBlogSample: rawBlogs[0] || null
      });
      
      // Map backend BlogSummaryResponse to frontend BlogPost format
      // Backend BlogSummaryResponse has: id (int), title, slug, excerpt, author_username, views, likes_count, comments_count, etc.
      const mappedBlogs: BlogPost[] = rawBlogs.map((blog: BackendBlogRaw, index: number) => {
        try {
          const mapped = mapBackendBlogToPost(blog);
          logger.debug(`Mapped blog ${index + 1}`, 'useFetchBlogs', { original: blog, mapped });
          return mapped;
        } catch (mapError) {
          logger.error(`Error mapping blog at index ${index}`, 'useFetchBlogs', { blog, error: mapError });
          return mapBackendBlogToPost({ id: blog.id, title: blog.title || 'Untitled' });
        }
      });
      
      logger.info('Successfully mapped blogs', 'useFetchBlogs', {
        originalCount: rawBlogs.length,
        mappedCount: mappedBlogs.length,
        total
      });
      
      setBlogs(mappedBlogs);
      setTotal(total);
      setPagination({
        page,
        per_page,
        total
      });
      
      return mappedBlogs;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch blogs';
      logger.error('Blog fetch error', 'useFetchBlogs', { 
        error: err,
        errorMessage,
        filters
      });
      setError(errorMessage);
      setBlogs([]);
      setTotal(0);
      return [];
    } finally {
      setLoading(false);
      logger.debug('Blog fetch completed', 'useFetchBlogs', { loading: false });
    }
  }, []);

  const getBlogById = useCallback(async (blogId: string, options?: { preview?: boolean }): Promise<BlogPost | null> => {
    logger.debug('Fetching blog by ID', 'useFetchBlogs', { blogId, preview: options?.preview });
    
    try {
      const response = await blogApiService.getBlogById(blogId, options);
      
      logger.debug('Received blog by ID response', 'useFetchBlogs', {
        success: response.success,
        hasData: !!response.data,
        blogId
      });
      
      if (!response.success || !response.data) {
        logger.error('Failed to fetch blog by ID', 'useFetchBlogs', {
          blogId,
          response
        });
        throw new Error('Failed to fetch blog');
      }
      
      logger.info('Successfully fetched blog by ID', 'useFetchBlogs', { blogId });
      return response.data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch blog';
      logger.error('Error fetching blog by ID', 'useFetchBlogs', {
        blogId,
        error: err,
        errorMessage
      });
      setError(errorMessage);
      return null;
    }
  }, []);

  const getBlogBySlug = useCallback(async (slug: string, options?: { preview?: boolean }): Promise<BlogPost | null> => {
    logger.debug('Fetching blog by slug', 'useFetchBlogs', { slug, preview: options?.preview });

    try {
      const response = await blogApiService.getBlogBySlug(slug, options);

      if (!response.success || !response.data) {
        logger.error('Failed to fetch blog by slug', 'useFetchBlogs', { slug, response });
        throw new Error('Failed to fetch blog');
      }

      logger.info('Successfully fetched blog by slug', 'useFetchBlogs', { slug });
      return response.data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch blog';
      logger.error('Error fetching blog by slug', 'useFetchBlogs', { slug, error: err, errorMessage });
      setError(errorMessage);
      return null;
    }
  }, []);

  return {
    blogs,
    total,
    pagination,
    loading,
    error,
    fetchBlogs,
    getBlogById,
    getBlogBySlug,
    clearError: () => setError(null)
  };
};
